import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '@kognitos/lattice';
import { useBoolean } from 'usehooks-ts';

import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import {
  useRetrieveAllWorkspaceBookConnections,
  useRetrieveLatestWorkspaceBooks,
} from '@/generated/hooks/bdk/v2/services/library';
import { IntegrationSelectionDialog } from '@/modules/integrations';
import { BOOK_CATALOG_STALE_TIME } from '@/modules/integrations/constants';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

interface IRailAddIntegrationButtonProps {
  onAddIntegration: (integration: Bdkv2BookDescriptor) => void;
}

/** The rail Integrations block's "+": opens the shared integration picker and
 *  hands the picked integration back to the editor. */
export function RailAddIntegrationButton({
  onAddIntegration,
}: IRailAddIntegrationButtonProps) {
  const { t: tAutomation } = useTranslation('automation');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const {
    value: isOpen,
    setTrue: openDialog,
    setFalse: closeDialog,
  } = useBoolean();
  const enabled = isOpen && Boolean(organizationId && workspaceId);

  const { data: booksData, isLoading: isLoadingBooks } =
    useRetrieveLatestWorkspaceBooks(
      organizationId,
      workspaceId,
      { page_size: 1000 },
      { query: { enabled, staleTime: BOOK_CATALOG_STALE_TIME } },
    );
  const { data: connectionsData, isLoading: isLoadingConnections } =
    useRetrieveAllWorkspaceBookConnections(
      organizationId,
      workspaceId,
      { page_size: 100 },
      { query: { enabled } },
    );

  const integrations = useMemo(() => booksData?.data.books ?? [], [booksData]);
  const connections = useMemo(
    () => connectionsData?.data.book_connections ?? [],
    [connectionsData],
  );

  const handleSelect = (integration: Bdkv2BookDescriptor) => {
    closeDialog();
    onAddIntegration(integration);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon-xs"
        aria-label={tAutomation('automation.draftRail.addIntegration')}
        onClick={openDialog}
      >
        <Icon type="Plus" size="xs" />
      </Button>
      <IntegrationSelectionDialog
        isOpen={isOpen}
        integrations={integrations}
        connections={connections}
        // A shared connection makes these usable in a draft right away.
        allowSuperseded
        isLoading={isLoadingBooks || isLoadingConnections}
        description={tAutomation(
          'automation.draftRail.addIntegrationDescription',
        )}
        onSelect={handleSelect}
        onCancel={closeDialog}
      />
    </>
  );
}
